/*
 * InstallPromptModal — full-screen "Install & Enable Notifications" gate.
 *
 * Blocks the page until the user either:
 *   (a) has installed the PWA to their home screen (or is running as standalone), OR
 *   (b) has explicitly tapped "Skip for now"
 *
 * Shows platform-detected instructions (iOS Safari vs. Android Chrome vs. desktop).
 * After install detection or skip, auto-prompts the enable-notifications flow.
 *
 * Legal disclosures per user request:
 *   - Message and data rate disclaimer
 *   - Privacy small print with link
 *   - Excited but honest tone
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";

const C = {
  bone: "#F9F8F6",
  slate: "#0F172A",
  slateDim: "#475569",
  forest: "#2B4C3B",
  line: "#E2E0D8",
  amber: "#B45309",
};

function detectPlatform() {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const isAndroid = /Android/.test(ua);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
  if (isIOS) return isSafari ? "ios-safari" : "ios-other";
  if (isAndroid) return "android";
  return "desktop";
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  // Android/Chrome/PWA: display-mode standalone
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS Safari legacy standalone flag
  if (window.navigator && window.navigator.standalone === true) return true;
  return false;
}

/**
 * Props:
 *   audience: "rider" | "driver"
 *   onSkip: () => void   — called when user taps "Skip for now"
 *   onInstalled: () => void — called when we detect standalone launch
 */
export default function InstallPromptModal({ audience = "rider", onSkip, onInstalled }) {
  const [installEvent, setInstallEvent] = useState(null);
  const [skipConfirm, setSkipConfirm] = useState(false);
  const platform = useMemo(detectPlatform, []);

  // Capture Android/Chrome's install prompt so we can trigger it programmatically.
  useEffect(() => {
    const handler = (e) => { e.preventDefault(); setInstallEvent(e); };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  // Re-check standalone after visibility change (user returns from install)
  useEffect(() => {
    try { window.__ASTAT_INSTALL_MODAL_MOUNTED__ = true; } catch { /* */ }
    const check = () => { if (isStandalone()) onInstalled && onInstalled(); };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    return () => {
      try { window.__ASTAT_INSTALL_MODAL_MOUNTED__ = false; } catch { /* */ }
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [onInstalled]);

  const triggerAndroidInstall = async () => {
    if (!installEvent) return;
    installEvent.prompt();
    try {
      const { outcome } = await installEvent.userChoice;
      if (outcome === "accepted") {
        // Re-check after a short delay — standalone becomes true asynchronously
        setTimeout(() => { if (isStandalone()) onInstalled && onInstalled(); }, 800);
      }
    } catch { /* silent */ }
    setInstallEvent(null);
  };

  const brand = audience === "driver" ? "MedexOVA Driver" : "MedexOVA Ride";
  const hook = audience === "driver"
    ? "Never miss a dispatch."
    : "Real-time driver alerts on your phone.";
  const perkList = audience === "driver" ? [
    { icon: "🔔", label: "New trip alerts — even with the app closed" },
    { icon: "🚦", label: "Instant dispatch when a rider hails you" },
    { icon: "💰", label: "Payout confirmations after each ride" },
    { icon: "📍", label: "One-tap access from your home screen" },
  ] : [
    { icon: "🚗", label: "Ping when your driver accepts" },
    { icon: "🚦", label: "Ping the moment they arrive" },
    { icon: "🧾", label: "Receipt with tap-to-rate the second you drop off" },
    { icon: "📍", label: "One-tap access from your home screen" },
  ];

  return (
    <div
      data-testid={`install-modal-${audience}`}
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(15, 23, 42, 0.72)",
        display: "grid", placeItems: "center",
        padding: 16, overflowY: "auto",
      }}
    >
      <div style={{
        background: C.bone, maxWidth: 460, width: "100%",
        borderRadius: 20, boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
        padding: 24, color: C.slate, maxHeight: "94vh", overflowY: "auto",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: `linear-gradient(135deg,${C.forest},#3A7BD5)`, display: "grid", placeItems: "center", color: "#fff", fontWeight: 800, fontSize: 20 }}>A</div>
          <div>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.18em", color: C.slateDim, fontWeight: 800 }}>Install to home screen</div>
            <div style={{ fontSize: 20, fontWeight: 800 }}>{brand}</div>
          </div>
        </div>

        <div style={{ fontSize: 15, fontWeight: 600, color: C.forest, marginTop: 4, marginBottom: 12 }}>
          {hook}
        </div>

        <ul style={{ listStyle: "none", padding: 0, margin: "0 0 14px 0" }}>
          {perkList.map((p) => (
            <li key={p.label} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "6px 0", fontSize: 13.5, color: C.slate }}>
              <span style={{ fontSize: 18 }}>{p.icon}</span>
              <span>{p.label}</span>
            </li>
          ))}
        </ul>

        {/* Platform-specific instructions */}
        {platform === "ios-safari" && (
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 800, marginBottom: 8 }}>iPhone / iPad · Safari</div>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
              <li>Tap the <b>Share</b> button <span style={{ display: "inline-block", padding: "1px 6px", border: `1px solid ${C.line}`, borderRadius: 4, fontSize: 12 }}>⬆</span> at the bottom of Safari</li>
              <li>Scroll down and tap <b>Add to Home Screen</b></li>
              <li>Tap <b>Add</b> in the top right</li>
              <li>Open MedexOVA from your home screen icon — you&apos;re in!</li>
            </ol>
          </div>
        )}
        {platform === "ios-other" && (
          <div style={{ background: "#FEF3C7", border: `1px solid ${C.amber}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.amber, fontWeight: 800, marginBottom: 6 }}>⚠️ Open in Safari</div>
            <div style={{ fontSize: 13 }}>Push notifications on iPhone only work when this page is opened in <b>Safari</b> (not Chrome / Firefox). Copy this link and paste it into Safari:</div>
            <div style={{ fontSize: 12, fontFamily: "monospace", background: "#fff", padding: 8, borderRadius: 6, marginTop: 8, wordBreak: "break-all", color: C.forest }}>
              {typeof window !== "undefined" ? window.location.href : ""}
            </div>
          </div>
        )}
        {platform === "android" && (
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 14, marginBottom: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 800, marginBottom: 8 }}>Android · Chrome</div>
            {installEvent ? (
              <>
                <div style={{ fontSize: 13, marginBottom: 10 }}>One tap and you&apos;re installed:</div>
                <button
                  onClick={triggerAndroidInstall}
                  data-testid="install-modal-android-install"
                  style={{ width: "100%", padding: "12px 16px", borderRadius: 10, background: C.forest, color: "#fff", border: "none", fontSize: 15, fontWeight: 800, cursor: "pointer" }}
                >
                  📲 Install {brand}
                </button>
              </>
            ) : (
              <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
                <li>Tap the <b>⋮</b> menu (top right of Chrome)</li>
                <li>Tap <b>Install app</b> or <b>Add to Home screen</b></li>
                <li>Confirm <b>Install</b></li>
                <li>Open MedexOVA from your home screen — done!</li>
              </ol>
            )}
          </div>
        )}
        {platform === "desktop" && (
          <div style={{ background: "#fff", border: `1px solid ${C.line}`, borderRadius: 12, padding: 16, marginBottom: 14 }}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.14em", color: C.slateDim, fontWeight: 800, marginBottom: 10 }}>
              Get MedexOVA on your phone
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: 14, alignItems: "center" }}>
              <div style={{ padding: 8, background: "#fff", border: `1px solid ${C.line}`, borderRadius: 8, display: "inline-flex" }}>
                <QRCodeSVG
                  value={typeof window !== "undefined" ? window.location.href : ""}
                  size={128}
                  level="M"
                  includeMargin={false}
                  data-testid="install-modal-qr"
                />
              </div>
              <div style={{ fontSize: 13, color: C.slate, lineHeight: 1.55 }}>
                <div style={{ fontWeight: 700, color: C.forest, marginBottom: 6 }}>Scan with your phone camera</div>
                Point your phone camera at the code. It opens this exact page in your phone&apos;s browser, where you can tap <b>Install</b> (Android) or <b>Share → Add to Home Screen</b> (iPhone). Same login, same data — just on your pocket.
                <div style={{ marginTop: 8, fontSize: 11, color: C.slateDim, fontFamily: "monospace", wordBreak: "break-all" }}>
                  {typeof window !== "undefined" ? window.location.href : ""}
                </div>
              </div>
            </div>
            <div style={{ marginTop: 10, fontSize: 12, color: C.slateDim, borderTop: `1px dashed ${C.line}`, paddingTop: 10 }}>
              You&apos;re on desktop — installation isn&apos;t required here. Notifications work in this browser too; just allow them when prompted.
            </div>
          </div>
        )}

        {/* Legal disclosures */}
        <div style={{ background: "#F5F3EA", border: `1px solid ${C.line}`, borderRadius: 8, padding: 10, fontSize: 11, color: C.slateDim, lineHeight: 1.5, marginBottom: 12 }}>
          <div style={{ fontWeight: 700, color: C.slate, marginBottom: 4 }}>Fine print</div>
          <div><b>Data rates:</b> Message and data rates from your wireless carrier may apply while using MedexOVA.</div>
          <div style={{ marginTop: 4 }}><b>Notifications:</b> By enabling notifications you consent to receive ride and dispatch alerts. You can turn them off any time in your browser or device settings.</div>
          <div style={{ marginTop: 4 }}><b>Privacy:</b> We only use your location and contact info to coordinate your rides. See our <Link to="/privacy" style={{ color: C.forest, fontWeight: 700, textDecoration: "underline" }}>Privacy Policy</Link> and <Link to="/terms" style={{ color: C.forest, fontWeight: 700, textDecoration: "underline" }}>Terms of Service</Link>.</div>
        </div>

        {/* Skip flow — two-tap so accidental dismiss is hard */}
        {!skipConfirm ? (
          <button
            onClick={() => setSkipConfirm(true)}
            data-testid="install-modal-skip-btn"
            style={{ width: "100%", padding: "10px 12px", borderRadius: 10, background: "transparent", color: C.slateDim, border: `1px dashed ${C.line}`, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
          >
            Skip for now — I&apos;ll miss the alerts
          </button>
        ) : (
          <div style={{ background: "#fff", border: `2px solid ${C.amber}`, borderRadius: 10, padding: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: C.amber, marginBottom: 6 }}>Are you sure?</div>
            <div style={{ fontSize: 12, color: C.slateDim, marginBottom: 10 }}>
              Without notifications, you&apos;ll only know about {audience === "driver" ? "new trips" : "your driver's status"} when this browser tab is open in the foreground. You can always install later.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button
                onClick={() => setSkipConfirm(false)}
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: C.forest, color: "#fff", border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer" }}
              >
                Never mind
              </button>
              <button
                onClick={onSkip}
                data-testid="install-modal-skip-confirm"
                style={{ flex: 1, padding: "10px 12px", borderRadius: 10, background: "transparent", color: C.slateDim, border: `1px solid ${C.line}`, fontSize: 13, fontWeight: 600, cursor: "pointer" }}
              >
                Skip anyway
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Helpers exported for callers who need to gate rendering.
export { isStandalone, detectPlatform };
